import Moment from 'moment';

import ReportType from '@/constants/reportTypeEnum';

/**
 * Retrieve Reports in loaded Inspection that are flagged to allow creating a CRL
 * @param {Object[VuexState]} state
 * @returns {Array[Report]}
 */
const crlEligibleInspectionReports = state => {
  if (!state.inspection || !state.inspection.reports) {
    return {};
  }
  return state.inspection.reports.filter(report => report.canCreateCrl === true);
};

/**
 * Retrieve `blocked` status for currently loaded Inspection
 * @param {Object[VuexState]} state
 * @returns {Boolean}
 */
const inspectionCreateListBlocked = state => {
  if (!state.inspection || !state.inspection.folderStatus) {
    return false;
  }
  return state.inspection.folderStatus.blocked;
};

/**
 * Retrieve `enabled` status for currently loaded Inspection's Company settings
 * @param {Object[VuexState]} state
 * @returns {Boolean}
 */
const inspectionEnableCreateRequestList = state => {
  if (!state.inspection
    || !state.inspection.company
    || !state.inspection.company.companyOptions) {
    return false;
  }
  return state.inspection.company.companyOptions.enableRequestList;
};

/**
 * Retrieve reports flagged as `Inspection Report` in currently loaded Inspection's Reports
 * @param {Object[VuexState]} state
 * @returns {Array[Report]}
 */
const inspectionReports = state => {
  if (!state.inspection || !state.inspection.reports) {
    return [];
  }
  return state.inspection.reports.filter(report => report.type === ReportType.INSPECTION_REPORT);
};

/**
 * Retrieve Request Lists for currently loaded Inspection
 * @param {Object[VuexState]} state
 * @returns {Array[Report]}
 */
const inspectionRequestLists = state => {
  if (!state.inspection || !state.inspection.requestLists) {
    return [];
  }
  return state.inspection.requestLists.lists;
};

/**
 * Retrieve Request Lists for currently loaded Inspection
 * @param {Object[VuexState]} state
 * @returns {Array[Report]}
 */
const inspectionAcceptsOnlinePayment = state => {
  if (!state.inspection
    || !state.inspection.company
    || !state.inspection.company.companyOptions) {
    return false;
  }
  return state.inspection.company.companyOptions.acceptsOnlinePayment;
};

/**
 * Retrieve street address (address1 & address2) of currently loaded Inspection
 * @param {Object[VuexState]} state
 * @returns {String}
 */
const inspectionStreetAddress = state => {
  if ((!state.inspection || !state.inspection.address)
  || (!state.inspection.address.address1 && !state.inspection.address.address2)) {
    return '';
  }
  if (state.inspection.address.address1 && state.inspection.address.address2) {
    return `${state.inspection.address.address1}, ${state.inspection.address.address2}`;
  }
  return (state.inspection.address.address1 ? `${state.inspection.address.address1}` : `${state.inspection.address.address2}`);
};

/**
 * Retrieve location (City, State PostalCode) of currently loaded Inspection
 * @param {Object[VuexState]} state
 * @returns {String}
 */
const inspectionLocation = state => {
  if (!state.inspection || !state.inspection.address) {
    return '';
  }
  if (!state.inspection.address.city) {
    return `${state.inspection.address.state} ${state.inspection.address.postalCode}`;
  }
  if (!state.inspection.address.state && !state.inspection.address.postalCode) {
    return state.inspection.address.city;
  }
  return `${state.inspection.address.city}, ${state.inspection.address.state} ${state.inspection.address.postalCode}`;
};

const inspectionUnviewedReports = state => {
  if (!state.inspection || !state.inspection.reports) {
    return [];
  }
  return state.inspection.reports.filter(report => report.viewed === false);
};

const inspectionUnviewedReportsLatestDate = (state, getters) => {
  if (getters.inspectionUnviewedReports.length === 0) {
    return Moment();
  }
  return Moment.max(getters.inspectionUnviewedReports.map(report => Moment(report.uploadDate)));
};

const inspectionLatestUnviewedReports = (state, getters) => {
  if (getters.inspectionUnviewedReports.length === 0) {
    return [];
  }
  return getters.inspectionUnviewedReports
    .filter(report => Moment(report.uploadDate)
      .isSame(getters.inspectionUnviewedReportsLatestDate));
};

/**
 * Retrieve primary report from currently loaded Inspection
 * @param {Object[VuexState]} state
 * @returns {Object[Report]
 */
const primaryInspectionReport = state => {
  if (!state.inspection || !state.inspection.reports) {
    return {};
  }
  return state.inspection.reports.find(report => report.primaryReport === true) || {};
};

/**
 * Retrieve latest inspection address
 * @param {Object[VuexState]} state
 * @return {Object[Inspection]}
 */
const latestInspectionAddress = state => {
  if (!state.inspections || state.inspections.length === 0) {
    return {};
  }
  const latestInspection = state.inspections[0];
  return {
    address1: latestInspection.address1,
    address2: latestInspection.address2,
    city: latestInspection.city,
    state: latestInspection.state,
    postalCode: latestInspection.postalCode,
    country: latestInspection.country,
  };
};


export default {
  crlEligibleInspectionReports,
  inspectionAcceptsOnlinePayment,
  inspectionCreateListBlocked,
  inspectionEnableCreateRequestList,
  inspectionLatestUnviewedReports,
  inspectionReports,
  inspectionRequestLists,
  inspectionStreetAddress,
  inspectionLocation,
  inspectionUnviewedReports,
  inspectionUnviewedReportsLatestDate,
  primaryInspectionReport,
  latestInspectionAddress,
};
